import { useQuery } from "react-query";
import { Location } from "../models";
import { ILocation } from "../types";
import { useCache } from "../context/CacheContext";
import { useWorkspace } from "../context/WorkspaceContext";
import { useUserSession } from "../context/UserContext";
import { listLocations } from "../data/api/locations";
import { queryKeys } from "../data/queryKeys";
import firebase from "firebase/compat/app";

type WhereTuple = [string, firebase.firestore.WhereFilterOp, string];

/**
 * Applies the legacy `additionalWhere` Firestore-style filters client-side so
 * the API-backed hook keeps the exact same filtering behaviour callers relied
 * on (currently only equality filters are used).
 */
const applyWhereFilters = (
  locations: Location[],
  additionalWhere: WhereTuple[],
): Location[] => {
  if (additionalWhere.length === 0) return locations;

  return locations.filter((location) =>
    additionalWhere.every(([field, operator, value]) => {
      const fieldValue = (location as unknown as Record<string, unknown>)[field];
      switch (operator) {
        case "==":
          return fieldValue === value;
        case "!=":
          return fieldValue !== value;
        default:
          return true;
      }
    }),
  );
};

/**
 * Stored customer locations, read through the REST API (previously a direct
 * Firestore query). Query key, cache-key invalidation and `Location[]` instance
 * return shape are preserved.
 */
const useLocations = (
  workspaceId?: string,
  additionalWhere: WhereTuple[] = [],
) => {
  const { cacheKey } = useCache();
  const { activeWorkspace } = useWorkspace();
  const { userSession, isAuthenticated } = useUserSession();
  const resolvedWorkspaceId = workspaceId ?? activeWorkspace?.workspaceId ?? "";
  const token = userSession?.token?.accessToken;

  return useQuery<Location[], Error>(
    queryKeys.locations(cacheKey, resolvedWorkspaceId, additionalWhere),
    async () => {
      const locations = await listLocations(token as string, resolvedWorkspaceId);
      const mapped = locations.map((location: ILocation) => new Location(location));
      return applyWhereFilters(mapped, additionalWhere);
    },
    {
      enabled: !!token && !!resolvedWorkspaceId && isAuthenticated(),
    },
  );
};

export default useLocations;
